import type {
  ClientCascadeLockPreview,
  ClientHomePlan,
  ClientHomePlanBaseData,
  ClientHomePlanHydrated,
  ClientHomePlanDiscount,
  ClientStudioDocMetadataForHomePlan,
} from "api-types/homePlans/homePlanResponses";
import type { ClientLocationPlan, ClientResolvedBrochureSource } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationHydrated } from "api-types/locations/locationsResponses";
import type { ClientLotHydrated, ClientLotDiscount } from "api-types/lots/lotsResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { getStudioDocMetadataForHomePlanURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import type { ReactNode } from "react";
import { createContext } from "react";

export type HomePlanCurrentPage = "overview" | "edit" | "activity";

interface HomePlanContextProps {
  location?: ClientLocationHydrated;
  locationPlan?: ClientLocationPlan;
  homePlan?: ClientHomePlanHydrated;
  lot?: ClientLotHydrated;
  currentPage?: HomePlanCurrentPage;
  updateHomePlan?: (hp: ClientHomePlan) => void;
  refreshHomePlan?: () => void;
  masterPlan?: ClientMasterPlan;
  studioDocMetaData?: ClientStudioDocMetadataForHomePlan;
  lotDiscounts: ClientLotDiscount[];
  homePlanDiscounts: ClientHomePlanDiscount[];
  homePlanStatsInFlight: boolean;
  cascadeLockPreview?: ClientCascadeLockPreview;
  brochureSource?: ClientResolvedBrochureSource;
}

export const HomePlanContext = createContext<HomePlanContextProps>({
  lotDiscounts: [],
  homePlanDiscounts: [],
  homePlanStatsInFlight: false,
});

interface HomePlanContextConsumerProps {
  children: ReactNode;
  currentPage: HomePlanCurrentPage;
}

export default function HomePlanContextProvider({ children, currentPage }: HomePlanContextConsumerProps) {
  const { builderId, homePlanId } = useAppRouterParams();

  const {
    data: homePlanBaseData,
    mutate,
    loading,
  } = useFetch<ClientHomePlanBaseData>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/base-data`,
    [builderId, homePlanId],
    {
      refreshInterval(latestData) {
        return latestData?.homePlan?.stats?.statsJobId ? 3000 : 0;
      },
    },
  );

  const { data: studioDocMetadata, mutate: mutateStudioDocMetadata } = useFetch<ClientStudioDocMetadataForHomePlan>(
    homePlanId ? getStudioDocMetadataForHomePlanURL(builderId as string, homePlanId) : "",
    [builderId, homePlanId],
  );

  const updateHomePlan = (homePlan: ClientHomePlan) => {
    void mutate(
      homePlanBaseData ? { ...homePlanBaseData, homePlan: { ...homePlanBaseData.homePlan, ...homePlan } } : undefined,
    );

    if (homePlan.detachedAt) {
      void mutateStudioDocMetadata(
        studioDocMetadata ? { ...studioDocMetadata, detached: !!homePlan.detachedAt } : undefined,
      );
    }
  };

  if (loading) {
    return null;
  }

  return (
    <HomePlanContext.Provider
      value={{
        location: homePlanBaseData?.community,
        locationPlan: homePlanBaseData?.communityPlan,
        homePlan: homePlanBaseData?.homePlan,
        homePlanStatsInFlight: !!homePlanBaseData?.homePlan?.stats?.statsJobId,
        lot: homePlanBaseData?.lot,
        currentPage,
        updateHomePlan,
        refreshHomePlan: () => void mutate(),
        masterPlan: homePlanBaseData?.masterPlan,
        studioDocMetaData: studioDocMetadata
          ? { ...studioDocMetadata, detached: !!homePlanBaseData?.homePlan?.detachedAt }
          : undefined,
        lotDiscounts: homePlanBaseData?.lotDiscounts ?? [],
        homePlanDiscounts: homePlanBaseData?.homePlanDiscounts ?? [],
        cascadeLockPreview: homePlanBaseData?.cascadeLockPreview,
        brochureSource: homePlanBaseData?.brochureSource,
      }}
    >
      {children}
    </HomePlanContext.Provider>
  );
}
